import { useCart } from "@/composables/marketplace/cart/cart";
import { assertSameCurrency } from "@/services/currencies/currenciesHelpers";
import { assertCartExists, hasSelectedPaymentMethod, hasSelectedShippingMethod } from "@/services/marketplace/cart/cartHelpers";
import type { SingleSellerCart } from "@/services/marketplace/cart/cartTypes";
import { assertAllItemsInSingleSellerCartOfSameCurrency } from "@/services/marketplace/cart/cartValidators";
import { createNumericPrice } from "@/services/prices/priceHelpers";
import { type MaybeRefOrGetter, toValue } from "vue";

export function useSingleSellerCart(cartInput: MaybeRefOrGetter<SingleSellerCart>) {
  const totalItemsPriceBeforeDiscount = computed(() => {
    const cart = toValue(cartInput);
    assertAllItemsInSingleSellerCartOfSameCurrency(cart);
    return cart.priceItemsTotalBeforeDiscount;
  });

  /** If no discount is applied then this is the same as totalItemsPriceBeforeDiscount */
  const totalItemsPriceAfterDiscount = computed(() => {
    const cart = toValue(cartInput);
    const priceBeforeDiscount = totalItemsPriceBeforeDiscount.value;
    if (cart.discount === null) {
      return priceBeforeDiscount;
    } else {
      assertSameCurrency(priceBeforeDiscount, cart.discount);

      const finalAmount = priceBeforeDiscount.price - cart.discount.price;
      const finalCurrencyId = priceBeforeDiscount.currencyId;
      return createNumericPrice(finalAmount, finalCurrencyId);
    }
  });

  const totalQuantity = computed((): number => {
    const cart = toValue(cartInput);
    let total = 0;
    cart.items.map(({ quantity }) => (total += quantity));
    return total;
  });

  const commissionPrice = computed(() => {
    const cart = toValue(cartInput);
    const { cart: fullCart } = useCart();
    assertCartExists(fullCart.value);
    const totalAmount = cart.priceCommission.price + fullCart.value?.priceCommissionFixedPart.price;
    const currencyId = cart.priceCommission.currencyId;
    // @todo check currencies are same
    return createNumericPrice(totalAmount, currencyId);
  });

  /**
   * Items + shipping + payment + commission
   * No discount counted in.
   **/
  const totalPriceBeforeDiscount = computed(() => {
    const itemsPrice = totalItemsPriceBeforeDiscount.value;
    const currencyId = itemsPrice.currencyId;

    const { cart: fullCart } = useCart();
    assertCartExists(fullCart.value);
    let total = itemsPrice.price ?? 0;

    if (hasSelectedPaymentMethod(fullCart.value)) {
      assertSameCurrency(itemsPrice, fullCart.value.selectedPaymentMethod.price);
      total += fullCart.value.selectedPaymentMethod.price.price;
    }

    if (hasSelectedShippingMethod(fullCart.value)) {
      assertSameCurrency(itemsPrice, fullCart.value.selectedShippingMethod.price);
      total += fullCart.value.selectedShippingMethod.price.price;
    }

    const priceCommission = commissionPrice.value;
    assertSameCurrency(itemsPrice, priceCommission);
    total += priceCommission.price;

    return createNumericPrice(total, currencyId);
  });

  /**
   * Items + shipping + payment + commission
   * Discount is counted in.
   * If no discount is applied then this is the same as totalPriceBeforeDiscount
   **/
  const totalPriceAfterDiscount = computed(() => {
    const itemsPrice = totalItemsPriceAfterDiscount.value;
    const currencyId = itemsPrice.currencyId;

    const { cart: fullCart } = useCart();
    assertCartExists(fullCart.value);
    let total = itemsPrice.price ?? 0;

    if (hasSelectedPaymentMethod(fullCart.value)) {
      assertSameCurrency(itemsPrice, fullCart.value.selectedPaymentMethod.price);
      total += fullCart.value.selectedPaymentMethod.price.price;
    }

    if (hasSelectedShippingMethod(fullCart.value)) {
      assertSameCurrency(itemsPrice, fullCart.value.selectedShippingMethod.price);
      total += fullCart.value.selectedShippingMethod.price.price;
    }

    const priceCommission = commissionPrice.value;
    assertSameCurrency(itemsPrice, priceCommission);
    total += priceCommission.price;

    return createNumericPrice(total, currencyId);
  });

  return {
    totalQuantity,
    totalItemsPriceBeforeDiscount,
    totalItemsPriceAfterDiscount,
    totalPriceBeforeDiscount,
    totalPriceAfterDiscount,
    commissionPrice,
  };
}
